import { Icon, Loading, VirtualGallery, ospThemes } from "@ospweb/sdk-ui";
import { useCallback, useEffect } from "react";
import Image from "next/image";
import { useSnapshot } from "valtio";
import { cn } from "utils";
import log from "logbeacon";
import { FormattedPrice } from "../PriceDisplay/FormattedPrice";
import { SIGNAL_LEVELS, getTALevelLabel } from "./const";
import { SignalLevelConfig, TAGridProps } from "./types";
import { TADetailWebModalCreateAble } from "./TADetailWebModal";
import { useTASectionData } from "@/src/hooks/useTASectionData";
import { useLoginModalProvider } from "@/src/hooks/useLoginModalProvider";
import { rootStore } from "@/src/store";
import { AgentReport } from "@/src/features/api/memberApi";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/TA/TAGrid";
import { formatTADate } from "@/src/utils";
import { EmptyState } from "@/src/components/Empty";

const I18n = I18nUtil.createScoped(Translations);

export const TAGrid = (props: TAGridProps) => {
  const {
    type,
    itemWidth = 357,
    itemHeight = 96,
    columnGap = 16,
    rowGap = 16,
    padding = 0,
    preloadItemCount = 9,
    enableInfiniteScroll = true,
    className,
    style,
    initialTAData,
    filterOption,
    filterStrongSignalsOnly = false,
  } = props;
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { open: openLoginModal } = useLoginModalProvider();

  const {
    displayData,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    hasAssetsForCurrentMarket,
    rawApiHasData,
    handleExploreNow,
  } = useTASectionData({
    type,
    filterOption,
    filterStrongSignalsOnly,
  });

  useEffect(() => {
    log.debug("[TAGrid] show");
    log.debug("[TAGrid] initialTAData", initialTAData);
  }, [initialTAData]);

  const renderItem = useCallback((item: AgentReport) => {
    const { content } = item;

    const levelConfig = SIGNAL_LEVELS.find(
      (level: SignalLevelConfig) => level.key === content?.level,
    );
    const isUp = (content?.ta_signal?.trend_analysis_change_percent ?? 0) > 0;

    return (
      <div
        data-openreplay-label="TA · TAGrid · div"
        key={item.id}
        className="w-full h-full bg-background_solid_neutral_primary p-4 rounded-3 cursor-pointer transition-shadow md:hover:shadow-[inset_0_0_0_1px_var(--edgen-border\_white\_transparent10)]"
        onClick={async () => {
          await TADetailWebModalCreateAble({ data: item });
        }}
      >
        <div className="flex items-center">
          <span className="body_medium_s text-content_secondary">
            {I18n.t("Change")}
          </span>

          <div className="flex flex-row gap-[2px] items-center ml-1">
            {content?.previous_level && (
              <>
                <span className="body_bold_s text-content_primary">
                  {getTALevelLabel(content?.previous_level)}
                </span>
                <Icon
                  name="right-arrow-alt"
                  size={12}
                  color={ospThemes.colors.content_primary}
                />
              </>
            )}
            <span
              className="body_bold_s text-content_primary rounded-1 px-[6px] py-[2px]"
              style={{
                color: levelConfig?.activeColor,
                backgroundColor: levelConfig?.bgColor,
              }}
            >
              {getTALevelLabel(content?.level)}
            </span>
          </div>
          {/* <span className="title_m text-content_primary">{symbol}</span> */}

          <span className={`body_s text-content_tertiary ml-2`}>
            {item?.created ? formatTADate(item?.created) : ""}
          </span>
        </div>
        <div className="flex flex-row mt-2">
          {item?.assets?.[0]?.logo && (
            <Image
              src={item?.assets?.[0]?.logo ?? ""}
              alt="signal-label-icon"
              className="w-8 h-8 border-[1px] border-border_solid rounded-full overflow-hidden"
              width={32}
              height={32}
            />
          )}
          <div className="flex flex-col ml-2">
            <span className="body_medium_m text-content_primary flex flex-row items-center gap-1">
              {item?.assets?.[0]?.symbol ?? ""}
              <span className="body_s text-content_tertiary max-w-[200px] truncate">
                {item?.assets?.[0]?.company_name ?? ""}
              </span>
            </span>
            <div className="flex flex-row">
              <span className="body_medium_s  text-content_primary">
                <FormattedPrice
                  price={content?.ta_signal?.price?.toString() || ""}
                  className={{
                    container: "body_medium_s text-content_primary",
                    zeroCount: "body_medium_xs",
                  }}
                  currencySymbol="$"
                />
              </span>
              <div className={`flex items-center ml-1`}>
                <Icon
                  name={isUp ? "upvote" : "downvote"}
                  size={12}
                  color={
                    isUp
                      ? ospThemes.colors.content_accent1_primary
                      : ospThemes.colors.content_accent2_primary
                  }
                />
                <span
                  className={cn("body_medium_s", {
                    "text-content_accent1_primary": isUp,
                    "text-content_accent2_primary": !isUp,
                  })}
                >
                  {content?.ta_signal?.trend_analysis_change_percent ?? "0.00"}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }, []);

  const handleLoadMore = useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage && !isLoading) {
      await fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isLoading]);

  return (
    <>
      {isLoading ? (
        <div className="w-full h-full flex justify-center items-center min-h-[300px]">
          <Loading fill={ospThemes.colors.content_tertiary} />
        </div>
      ) : displayData.length > 0 ? (
        <VirtualGallery
          data={displayData}
          renderItem={renderItem}
          itemWidth={itemWidth}
          itemHeight={itemHeight}
          columnGap={columnGap}
          rowGap={rowGap}
          padding={padding}
          preloadItemCount={preloadItemCount}
          enableInfiniteScroll={enableInfiniteScroll}
          onLoadMore={handleLoadMore}
          loadingMore={isFetchingNextPage}
          hasMore={hasNextPage}
          // noMoreContent={noMoreContent}
          className={className}
          style={style}
        />
      ) : (
        (() => {
          if (filterOption === "Watchlist" && !isLoggedIn) {
            return (
              <div className="flex w-full flex-col items-center justify-center gap-3 px-10 py-6 text-center min-h-[436px]">
                <EmptyState
                  className="max-w-[313px]"
                  title={I18n.t("Log in to view your watchlist.")}
                  description=" "
                  titleClassName="title_m text-content_secondary mt-0 mb-1"
                  descriptionClassName="body_s text-content_tertiary"
                />
                <button
                  aria-label="TAGrid"
                  data-openreplay-label={`TAGrid Button | ${I18n.t("Log in")}`}
                  id="btn-tagrid"
                  type="button"
                  onClick={() => openLoginModal?.()}
                  className={cn(
                    "button_s rounded-[6px] px-2 py-1.5",
                    "bg-background_white_overlay100 text-content_inverse_primary",
                  )}
                >
                  {I18n.t("Log in")}
                </button>
              </div>
            );
          }
          if (
            filterOption === "Watchlist" &&
            !rawApiHasData &&
            !hasAssetsForCurrentMarket
          ) {
            return (
              <div className="flex w-full flex-col items-center justify-center gap-3 px-10 py-6 text-center">
                <EmptyState
                  className="max-w-[313px]"
                  title={I18n.t("Your Watchlist is Empty")}
                  description={I18n.t(
                    "Follow stocks or crypto to get real-time TA signals and Alpha insights.",
                  )}
                  titleClassName="title_m text-content_secondary mt-0 mb-1"
                  descriptionClassName="body_s text-content_tertiary"
                />
                <button
                  aria-label="TAGrid"
                  data-openreplay-label={`TAGrid Button | ${I18n.t("Explore Now")}`}
                  id="btn-tagrid-2"
                  type="button"
                  onClick={handleExploreNow}
                  className={cn(
                    "button_s rounded-[6px] px-2 py-1.5",
                    "bg-background_white_overlay100 text-content_inverse_primary",
                  )}
                >
                  {I18n.t("Explore Now")}
                </button>
              </div>
            );
          }
          return (
            <div className="w-full h-full overflow-y-auto no-scrollbar flex pt-[121px] justify-center min-h-[436px]">
              <span className="body_m text-content_quaternary">
                {I18n.t("No active signals")}
              </span>
            </div>
          );
        })()
      )}
    </>
  );
};
